import { Link } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <Mail className="w-8 h-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">FormTo.cc</span>
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-gray-500 mb-8">Last updated: January 6, 2025</p>

        <div className="prose prose-gray max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Overview</h2>
            <p className="text-gray-600 mb-4">
              FormTo.cc ("we", "our", or "us") operates a form-to-Telegram notification service.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your
              information when you use our website and services.
            </p>
            <p className="text-gray-600">
              We are committed to protecting your privacy and handling your data transparently.
              Please read this policy carefully to understand our practices.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information We Collect</h2>

            <h3 className="text-xl font-medium text-gray-800 mb-3">Account Information</h3>
            <p className="text-gray-600 mb-4">
              When you create an account, we collect:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Email address (used for authentication and account recovery)</li>
              <li>Password (stored securely using AWS Cognito)</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3">Form Submission Data</h3>
            <p className="text-gray-600 mb-4">
              When form submissions are sent to our webhooks, we collect:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Form field data submitted by website visitors</li>
              <li>IP address of the submitter</li>
              <li>User agent (browser information)</li>
              <li>Timestamp of submission</li>
              <li>Origin URL (the website sending the submission)</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3">Telegram Connection Data</h3>
            <p className="text-gray-600 mb-4">
              When you connect a Telegram chat, we store:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Telegram chat ID</li>
              <li>Chat type (private, group, or channel)</li>
              <li>Chat title (for groups and channels)</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3">Automatically Collected Information</h3>
            <p className="text-gray-600 mb-4">
              We automatically collect certain information when you visit our website:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Log data (IP address, browser type, pages visited)</li>
              <li>Device information</li>
              <li>Cookies for authentication and session management</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
            <p className="text-gray-600 mb-4">We use the collected information to:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Provide and maintain our services</li>
              <li>Process and deliver form submissions to your Telegram chats</li>
              <li>Authenticate users and secure accounts</li>
              <li>Send service-related communications</li>
              <li>Enforce rate limits and prevent abuse</li>
              <li>Improve and optimize our services</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Storage and Retention</h2>
            <p className="text-gray-600 mb-4">
              <strong>Form Submissions:</strong> Stored for 90 days, then automatically deleted.
            </p>
            <p className="text-gray-600 mb-4">
              <strong>Account Data:</strong> Retained as long as your account is active. You can
              request deletion of your account and associated data at any time.
            </p>
            <p className="text-gray-600">
              <strong>Infrastructure:</strong> We use AWS services (Lambda, DynamoDB, RDS, Cognito)
              hosted in secure data centers. All data is encrypted in transit and at rest.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Sharing and Disclosure</h2>
            <p className="text-gray-600 mb-4">
              We do not sell, trade, or rent your personal information to third parties.
              We may share information only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>
                <strong>Service Providers:</strong> With trusted third-party services (AWS, Telegram)
                necessary to operate our platform
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law, court order, or
                governmental request
              </li>
              <li>
                <strong>Protection of Rights:</strong> To protect our rights, privacy, safety,
                or property
              </li>
              <li>
                <strong>Business Transfers:</strong> In connection with a merger, acquisition,
                or sale of assets
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cookies</h2>
            <p className="text-gray-600 mb-4">
              We use cookies and similar technologies for:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Authentication and session management</li>
              <li>Remembering your preferences</li>
              <li>Security purposes</li>
            </ul>
            <p className="text-gray-600 mt-4">
              You can control cookies through your browser settings. Disabling cookies may
              affect the functionality of our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights</h2>
            <p className="text-gray-600 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Export your data in a portable format</li>
              <li>Withdraw consent for data processing</li>
              <li>Lodge a complaint with a supervisory authority</li>
            </ul>
            <p className="text-gray-600 mt-4">
              To exercise these rights, please contact us at{' '}
              <a href="mailto:privacy@formto.cc" className="text-blue-600 hover:underline">
                privacy@formto.cc
              </a>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">International Data Transfers</h2>
            <p className="text-gray-600">
              Our services are hosted on AWS infrastructure. Your data may be processed in
              regions where AWS operates data centers. We ensure appropriate safeguards are
              in place for international data transfers in compliance with applicable data
              protection laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Security</h2>
            <p className="text-gray-600">
              We implement industry-standard security measures to protect your data, including:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mt-4 space-y-2">
              <li>Encryption in transit (TLS/HTTPS)</li>
              <li>Encryption at rest for stored data</li>
              <li>Secure authentication via AWS Cognito</li>
              <li>Origin validation for form submissions</li>
              <li>Rate limiting to prevent abuse</li>
              <li>Regular security reviews</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-600">
              Our services are not directed to individuals under 16. We do not knowingly
              collect personal information from children. If you believe we have collected
              data from a child, please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Policy</h2>
            <p className="text-gray-600">
              We may update this Privacy Policy from time to time. We will notify you of
              any significant changes by posting the new policy on this page and updating
              the "Last updated" date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-600">
              If you have questions about this Privacy Policy or our data practices,
              please contact us at:
            </p>
            <ul className="list-none mt-4 text-gray-600 space-y-2">
              <li>
                Email:{' '}
                <a href="mailto:privacy@formto.cc" className="text-blue-600 hover:underline">
                  privacy@formto.cc
                </a>
              </li>
              <li>
                Website:{' '}
                <a href="https://formto.cc" className="text-blue-600 hover:underline">
                  https://formto.cc
                </a>
              </li>
            </ul>
          </section>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="bg-gray-100 border-t border-gray-200 py-8">
        <div className="container mx-auto px-4 text-center text-gray-600">
          <p>&copy; 2025 FormTo.cc. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <Link to="/privacy" className="text-blue-600 hover:underline">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-blue-600 hover:underline">
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
